import React, { useEffect, useState } from "react";
import { styled, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import MuiDrawer from "@mui/material/Drawer";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";
import CssBaseline from "@mui/material/CssBaseline";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Grid from "@mui/material/Grid2";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import xomeLogo from "../../assets/xome-logo-blaze.png";
import { Close } from "@mui/icons-material";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  leftDrawerMenu,
  closeMenuBar,
  openMenuBar,
  openLiveAuction,
} from "../../redux/slice/menuSlice";
import IconList from "../../utils/common/Iconlist";
import { useAuth } from "../../services/AuthContext";
import { useDispatch, useSelector } from "react-redux";
import signoutBtn from "../../assets/signout.png";
import { Tooltip, tooltipClasses } from "@mui/material";
import { LiaSignOutAltSolid } from "react-icons/lia";
import { useMsal } from "@azure/msal-react";
import { resetAll } from "../../redux/slice/userSlice";
import MenuIconImg from "../../assets/MenuIcon.svg";
import { RootState } from "@/redux/store";
import { handleUnload } from "../../routes/ProtectedRoute";
import { handleFetchApi } from "../../utils/api";
import { getFcltAppUrl, getUserApiUrl } from "../../utils/config";
import impersonating from "../../assets/Tag.png"

const drawerWidth = 280;

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(["width", "margin"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth * 0.98}px)`,
    transition: theme.transitions.create(["width", "margin"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const DesktopDrawer = styled(MuiDrawer, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  ...(open && {
    ...openedMixin(theme),
    "& .MuiDrawer-paper": { ...openedMixin(theme), overflowY: "unset" },
  }),
  ...(!open && {
    ...closedMixin(theme),
    "& .MuiDrawer-paper": { ...closedMixin(theme), overflowY: "unset" },
  }),
}));

const CustomTooltip = styled(({ className, ...props }) => (
  <Tooltip placement="right" {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: theme.palette.common.white,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.common.white,
    color: "#3B3B3B",
    position: "realative",
    top: "14px",
    left: "0",
  },
}));

export default function Sidebar({ open, setOpen }) {
  const { instance } = useMsal();
  const isDrawerOpen = useSelector((store) => store.menu.isOpen);
  const menuItems = useSelector((store) => store.menu.menuItems);
  const userData = useSelector((store: RootState) => store.user.loginUserDetails)
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const [menuData, setMenuData] = useState();
  const [activeId, setActiveId] = useState(1);
  const { investorId } = useAuth();
  const { pathname } = useLocation();

  const localProperties = useSelector(
    (state) => state?.mybid?.myBidUpdates || []
  );
  const dynamicProperties = useSelector(
    (state) => state.mybid.myBidData.data?.Today || []
  );

  const properties =
    dynamicProperties.length == 0 ? localProperties : dynamicProperties;

  useEffect(() => {
    setOpen(isDrawerOpen);
  }, [isDrawerOpen]);

  const appendIcons = (menu, icons) => {
    // Convert icons array into a Map for O(1) lookups
    const iconMap = new Map(
      icons.map((iconItem) => [iconItem.id, iconItem.icon])
    );

    // Create a Map to group subitems by parentId
    const subItemsMap = new Map();
    menu.forEach((item) => {
      if (item.parentId) {
        if (!subItemsMap.has(item.parentId)) {
          subItemsMap.set(item.parentId, []);
        }
        subItemsMap.get(item.parentId).push(item);
      }
    });

    // Process the main menu items
    return menu
      .filter((item) => !item.parentId) // Filter out subitems
      .map((item) => {
        // Find the icon for the current item
        const foundIcon = iconMap.get(item.iconId);

        // Find subitems from the pre-processed Map
        const moreOptions = (subItemsMap.get(item.id) || []).map((subItem) => ({
          ...subItem,
          icon: iconMap.get(subItem.iconId) || null,
        }));

        if (item.menuName === "MORE") {
          return {
            ...item,
            icon: foundIcon || null,
            iconOpen: <ChevronRightIcon />,
            ...(moreOptions.length > 0 && { moreOptions }), // Add moreOptions only if subitems exist
          };
        }
        // Append subitems (moreOptions) only if they exist
        return {
          ...item,
          icon: foundIcon || null,
        };
      });
  };

  useEffect(() => {
    if (menuItems?.length > 0) {
      const updatedMenuData = appendIcons(menuItems, IconList);
      setMenuData(updatedMenuData);
    }
  }, [menuItems]);
  useEffect(() => {
    const fetchdata = async () => {
      try {
        await dispatch(leftDrawerMenu()).unwrap();
      } catch (error) {
        console.error("Failed to load menu:", error);
      }
    };
    fetchdata();
    // return () => {
    //   setMenuData(null);
    // };
  }, []);

  const handleMoreToggle = (id) => {
    setMoreOpen((prev) => !prev);
  };

  const handleDrawerOpen = (e) => {
    e.preventDefault();
    dispatch(openMenuBar());
    // setOpen(true);
  };

  const handleDrawerClose = (e) => {
    dispatch(closeMenuBar());
    // setOpen(false);
  };

  const handleNavigate = (url, id) => {
    sessionStorage.removeItem("propertyList")

    setActiveId(id);
    navigate(url);
    if (id == 2) {
      dispatch(openLiveAuction());
    }
  };

  const handleLogout = async () => {
    const isImpersonate = sessionStorage.getItem("isImpersonate")
    await handleFetchApi({
      method: "POST",
      endpoint: "auth/logout",
      baseUrl: getUserApiUrl()
    })
    if (isImpersonate === "true") {
      const isLocalhost = window.location.hostname === "localhost"
      const portNumber = window.location.port === "3001" ? 3000 : 3001
      // The origin of the other app expecting the message 
      const targetOrigin = isLocalhost
        ? `http://localhost:${portNumber}`
        : getFcltAppUrl();
      // The message you want to send back
      const message = { status: "sessionClosed", logout: true };

      if (window.opener) {
        window.opener.postMessage(message, targetOrigin);
      } else if (window.parent !== window) {
        window.parent.postMessage(message, targetOrigin);
      } else {
        console.log("No parent or opener to send message to.");
      }
      sessionStorage.clear();
      window.close()
    } else {
      dispatch(resetAll());
      sessionStorage.clear();
      instance.logoutRedirect();
      instance.clearCache();
    }


  };
  const serverListItem = (
    <List sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Other ListItems for menuData */}
      {menuData &&
        menuData?.map((option) => (
          <ListItem
            key={option.id}
            disablePadding
            sx={{
              display: "block",
            }}
          >
            {!isDrawerOpen ? (
              <CustomTooltip title={option.menuName}>
                <ListItemButton
                  sx={{
                    minHeight: 48,
                    backgroundColor:
                      moreOpen && option.menuName === "MORE"
                        ? "#FFFFF !important"
                        : pathname.includes(option.navigationUrl)
                          ? "#FF5C3926 !important"
                          : "transparent",
                    justifyContent: open ? "initial" : "center",
                    px: 2.5,
                    columnGap: 1,
                  }}
                  onClick={
                    option.menuName === "MORE"
                      ? () => handleMoreToggle(option.id)
                      : () => handleNavigate(option.navigationUrl, option.id)
                  }
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      justifyContent: "center",
                      "& .MuiSvgIcon-fontSizeMedium": {
                        fontWeight: 500,
                        color:
                          moreOpen && option.menuName === "MORE"
                            ? "#FF5C39"
                            : "#3B3B3B",
                      },
                    }}
                  >
                    {option.iconOpen && !moreOpen
                      ? option.iconOpen
                      : option.icon}
                  </ListItemIcon>
                  {open && (
                    <ListItemText
                      id={option.id}
                      primary={option.menuName}
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiListItemText-primary": {
                          fontSize: "16px",
                          fontWeight: 600,
                          fontFamily: "Poppins",
                          color:
                            moreOpen && option.menuName === "MORE"
                              ? "#FF5C39"
                              : "#3B3B3B",
                        },
                      }}
                    />
                  )}
                </ListItemButton>
              </CustomTooltip>
            ) : (
              <ListItemButton
                sx={{
                  minHeight: 48,
                  backgroundColor:
                    moreOpen && option.menuName === "MORE"
                      ? "#FFFFFF !important"
                      : pathname.includes(option.navigationUrl)
                        ? "#FF5C3926 !important"
                        : "transparent",
                  justifyContent: open ? "initial" : "center",
                  px: 2.5,
                  columnGap: 1,
                }}
                onClick={
                  option.menuName === "MORE"
                    ? () => handleMoreToggle(option.id)
                    : () => {
                      handleNavigate(option.navigationUrl, option.id)
                      isMobile && dispatch(closeMenuBar());
                    }
                }
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    justifyContent: "center",
                    "& .MuiSvgIcon-fontSizeMedium": {
                      fontWeight: 500,
                      color:
                        moreOpen && option.menuName === "MORE"
                          ? "#FF5C39"
                          : "#3B3B3B",
                    },
                  }}
                >
                  {option.iconOpen && !moreOpen ? option.iconOpen : option.icon}
                </ListItemIcon>
                {open && (
                  <>
                    <ListItemText
                      primary={option.menuName}
                      sx={{
                        opacity: open ? 1 : 0,
                        "& .MuiListItemText-primary": {
                          fontSize: "16px",
                          fontWeight: 600,
                          fontFamily: "Poppins",
                          color:
                            moreOpen && option.menuName === "MORE"
                              ? "#FF5C39"
                              : "#3B3B3B",
                        },
                      }}
                    />
                    {option.menuName === "LIVE AUCTIONS" &&
                      properties?.length > 0 &&
                      properties?.some(
                        (property) => property?.auctionStatus === "Live"
                      ) && (
                        <div className="flex items-center ml-1 text-[14px] font-semibold text-[#37ae37] border border-[#28A745] bg-[#28A7451A] rounded px-2 py-1">
                          <div className="rounded-full w-2 h-2 border border-[#28A745] bg-[#28A745] mr-1"></div>
                          Live
                        </div>
                      )}
                  </>
                )}
              </ListItemButton>
            )}
            {/* Conditional Rendering of More Content */}
            {option.menuName === "MORE" && moreOpen && (
              <Box sx={{ marginTop: 0.5 }}>
                {option.moreOptions.map((moreOptions) =>
                  !isDrawerOpen ? (
                    <CustomTooltip title={moreOptions.menuName}>
                      <ListItemButton
                        key={moreOptions.id}
                        sx={{
                          minHeight: 48,
                          justifyContent: open ? "initial" : "center",
                          pr: 2.5,
                          pl: open ? 6 : 2.5,
                          backgroundColor:
                            activeId === moreOptions.id
                              ? "#FF5C3926 !important"
                              : "transparent",
                          columnGap: 1,
                        }}
                        onClick={() =>
                          handleNavigate(
                            moreOptions.navigationUrl,
                            moreOptions.id,
                          )
                        }
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            justifyContent: "center",
                            "& .MuiSvgIcon-fontSizeMedium": {
                              fontWeight: 500,
                              color: "#3B3B3B",
                            },
                          }}
                        >
                          {moreOptions.icon}
                        </ListItemIcon>
                        {open && (
                          <ListItemText
                            primary={moreOptions.menuName}
                            sx={{
                              opacity: open ? 1 : 0,
                              "& .MuiListItemText-primary": {
                                fontSize: "16px",
                                fontWeight: 600,
                                fontFamily: "Poppins",
                                color: "#3B3B3B",
                              },
                            }}
                          />
                        )}
                        {option.rightIcon && open && option.rightIcon}
                      </ListItemButton>
                    </CustomTooltip>
                  ) : (
                    <ListItemButton
                      key={moreOptions.id}
                      sx={{
                        minHeight: 48,
                        justifyContent: open ? "initial" : "center",
                        pr: 2.5,
                        pl: open ? 6 : 2.5,
                        backgroundColor:
                          activeId === moreOptions.id
                            ? "#FF5C3926 !important"
                            : "transparent",
                        columnGap: 1,
                      }}
                      onClick={() => {
                        handleNavigate(
                          moreOptions.navigationUrl,
                          moreOptions.id
                        )
                        isMobile && dispatch(closeMenuBar());
                      }
                      }
                    >
                      <ListItemIcon
                        sx={{
                          minWidth: 0,
                          justifyContent: "center",
                          "& .MuiSvgIcon-fontSizeMedium": {
                            fontWeight: 500,
                            color: "#3B3B3B",
                          },
                        }}
                      >
                        {moreOptions.icon}
                      </ListItemIcon>
                      {open && (
                        <ListItemText
                          primary={moreOptions.menuName}
                          sx={{
                            opacity: open ? 1 : 0,
                            "& .MuiListItemText-primary": {
                              fontSize: "16px",
                              fontWeight: 600,
                              fontFamily: "Poppins",
                              color: "#3B3B3B",
                            },
                          }}
                        />
                      )}
                      {option.rightIcon && open && option.rightIcon}
                    </ListItemButton>
                  )
                )}
              </Box>
            )}
          </ListItem>
        ))}

      {/* Logout Item at the Bottom */}
      <ListItem
        disablePadding
        sx={{
          display: "flex",
          marginTop: "auto",
          justifyContent: "flex-end",
          position: "fixed",
          bottom: 0,
        }}
      >
        <ListItemButton
          sx={{
            minHeight: 48,
            backgroundColor: "transparent",
            justifyContent: open ? "initial" : "center",
            px: 2.5,
            columnGap: 1,
            position: "fixed",
            bottom: isMobile ? 0 : "20px",
            left: 4,
          }}
          aria-label="Signout"
          onClick={handleLogout} // Function to handle logout
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              justifyContent: "center",
            }}
          >
            {/* Inline SVG for logout icon */}
            <svg
              aria-label="logout-image"
              width="22"
              height="21"
              viewBox="0 0 22 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0" // Prevent icon from shrinking
            >
              <path
                d="M17 6.5L21 10.5M21 10.5L17 14.5M21 10.5H8M14 2.70404C12.7252 1.93827 11.2452 1.5 9.66667 1.5C4.8802 1.5 1 5.52944 1 10.5C1 15.4706 4.8802 19.5 9.66667 19.5C11.2452 19.5 12.7252 19.0617 14 18.296"
                stroke="#3B3B3B"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </ListItemIcon>
          {open && (
            <ListItem sx={{ p: 0 }}>
              <ListItemText
                primary="SIGN OUT"
                sx={{
                  opacity: open ? 1 : 0,
                  "& .MuiListItemText-primary": {
                    fontSize: "16px",
                    fontWeight: 600,
                    fontFamily: "Poppins",
                    color: "#3B3B3B",
                  },
                }}
              />
            </ListItem>
          )}
        </ListItemButton>
      </ListItem>
    </List>
  );
  const isImpersonate = sessionStorage.getItem("isImpersonate")
  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />
      {/* Custom Navbar */}
      <AppBar
        sx={{
          backgroundColor: "#FFF",
          boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
          top: isImpersonate ? 35 : 0,
        }}
        open={isMobile ? undefined : open}
      >
        <Toolbar>
          <IconButton
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            sx={{
              display: { xs: "block", sm: "none" },
              outline: "none",
              "&:focus": {
                outline: "none",
              },
              "&:focusVisible": {
                outline: "none",
              },
            }}
          >
            {/* <MenuIcon /> */}
            <img src={MenuIconImg} alt="menu" />
          </IconButton>
          <Grid
            container
            sx={{ width: "100%", py: { md: 0, xs: 2 } }}
            justifyContent={{ md: "space-between", sm: "space-between", xs: "end" }}
          >
            <Grid
              size={{ xs: 9, sm: 4 }}
              sx={{
                display: "flex",
                justifyContent: isMobile && "center",
                alignItems: "center",
              }}
            >
              {isMobile ? (
                <img
                  src={xomeLogo}
                  style={{
                    width: "30%",
                    height: "auto",
                    pr: { md: "auto", xs: 5 },
                  }}
                  alt="Logo"
                />
              ) : (
                <div className={`flex flex-row md:gap-x-16 sm:gap-x-2 xs:gap-x-16 p-1.5 md:justify-center sm:justify-end items-center ${isDrawerOpen ? 'sm:ml-[0rem]' : 'sm:ml-[6rem]'} md:ml-0 xs:ml-0`}>
                  {!open && (
                    <img
                      src={xomeLogo}
                      style={{ width: "20%", height: "auto" }}
                      alt="Logo"
                    />
                  )}

                  <Typography
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "Poppins",
                      color: "#3B3B3B",
                      marginTop: 2,
                      textWrap: { sm: "nowrap", xs: "none" }
                    }}
                    variant="body1"
                  >{`Welcome, ${isImpersonate === "true" ? userData?.ProfileName?.length > 2 ? userData?.ProfileName : userData?.BusinessAccounts?.length> 0 && userData?.BusinessAccounts[0]?.ProfileName || userData?.FirstName + " " + userData?.LastName : investorId?.username}`}
                  </Typography>


                </div>
              )}
            </Grid>
            <Grid xs={5} sm="auto" sx={{ display: "flex" }}>
              <NavLink
                to="/managefunds"
                style={({ isActive }) => ({
                  borderBlockEnd: isActive ? "2px solid #FF5C39" : "",
                  textDecoration: "none",
                })}
              >
                <IconButton
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    ":hover": {
                      backgroundColor: "transparent",
                    },
                    fill: "inherit",
                  }}
                  component="div"
                  aria-label="Manage Funds" // Added aria-label for screen readers
                  role="link" // Ensures the IconButton is recognized as a clickable link
                >
                  <AccountBalanceWalletOutlinedIcon
                    aria-label="accountBalanceWalletOutlinedIcon-svg"
                    sx={{ height: 31 }}
                    style={{
                      fill: pathname.includes("managefunds")
                        ? "#FF5C39"
                        : "#3B3B3B",
                    }}
                  />
                  <Typography
                    variant="caption"
                    sx={{
                      fontSize: "14px",
                      fontWeight: 500,
                      fontFamily: "Poppins",
                      color: pathname.includes("managefunds")
                        ? "#FF5C39"
                        : "#3B3B3B",
                      display: { xs: "none", sm: "block" },
                    }}
                  >
                    Manage Funds
                  </Typography>
                </IconButton>
              </NavLink>
              {/* <IconButton
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  ":hover": {
                    backgroundColor: "transparent",
                  },
                }}
              >
                <NotificationsNoneIcon sx={{ height: 31 }} aria-label="notification-svg"/>
                <Typography
                  sx={{
                    fontSize: "14px",
                    fontWeight: 500,
                    fontFamily: "Poppins",
                    color: "#3B3B3B",
                    display: { xs: "none", sm: "block" },
                  }}
                  variant="caption"
                >
                  Notifications
                </Typography>
              </IconButton> */}
            </Grid>
          </Grid>
        </Toolbar>
      </AppBar>
      {/* navbar End */}

      {/* Mobile Drawer Started */}

      {isMobile ? (

        <MuiDrawer
          sx={{
            width: open ? drawerWidth : 0,
            flexShrink: 0,
            zIndex: open ? 1201 : 0,
            "& .MuiDrawer-paper": {
              width: open ? drawerWidth : 0,
              boxSizing: "border-box",
            },
          }}
          variant="temporary"
          anchor="left"
          open={open}
          onClose={handleDrawerClose}
        >

          <DrawerHeader
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              width: "100%",
              padding: "8px",
              borderBottom: 1,
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                fontFamily: "Poppins",
                color: "#3B3B3B",
                marginTop: 2,
                textWrap: { sm: "nowrap", xs: "none" }
              }}
              variant="body1"
            >{`Welcome, ${isImpersonate === "true" ? userData?.ProfileName?.length > 2 ? userData?.ProfileName : userData?.BusinessAccounts[0]?.ProfileName || userData?.FirstName + " " + userData?.LastName : investorId?.username}`}
            </Typography>
            <IconButton onClick={handleDrawerClose}>
              <Close />
            </IconButton>
          </DrawerHeader>
          <List>{serverListItem}</List>
        </MuiDrawer>
      ) : (
        // Desktop Drawer Started
        <DesktopDrawer
          variant={"permanent"}
          open={open}
          slotProps={{
            paper: {
              sx: { top: isImpersonate ? 35 : 0, }
            }
          }}
          sx={{
            zIndex: 9999,

            "&.MuiDrawer-paperAnchorDockedLeft": {
              overflowY: "auto",
            },
          }}
        >
          <DrawerHeader
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              width: "100%",
              padding: "8px",
              top: isImpersonate ? 35 : 0,
            }}
          >
            {open && (
              <img
                src={xomeLogo}
                style={{ width: "40%", height: "auto" }}
                alt="Logo"
              />
            )}
          </DrawerHeader>

          <IconButton
            aria-label="toggle drawer"
            onClick={open ? handleDrawerClose : handleDrawerOpen}
            sx={{
              position: "absolute",
              top: "15%",
              left: open ? `calc(${drawerWidth}px - 15px)` : `50px`,
              transform: "translateY(-50%)",
              backgroundColor: "#f5f5f5",
              color: "#3B3B3B",
              borderRadius: "50%",
              boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
              zIndex: 1301,
              width: 30,
              height: 30,
              "&:hover": {
                backgroundColor: "#f5f5f5",
                color: "#3B3B3B",
              },
              outline: "none",
              "&:focus": {
                outline: "none",
              },
              "&:focusVisible": {
                outline: "none",
              },
              display: { xs: "none", sm: "flex" },
            }}
          >
            {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>

          <List
            sx={{
              paddingTop: "38px",
              position: "relative",
              minHeight: "90vh", // Ensure this takes up the full height
              maxHeight: "calc(100vh - 70px)", // Allow space for the sign-out button
              overflowY: "auto", // Enable vertical scrolling

            }}
          >
            {serverListItem}
          </List>
        </DesktopDrawer>
      )}
    </Box>
  );
}
